import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useViewSettings } from '../../../../context/ViewSettingsContext';
import { useTagSettings } from '../../../../context/TagSettingsContext';
import { useMIA } from '../../../../context/MIAContext';

// Atomic Design Components - Modal specific
import ArticleModalView from './ArticleModalView';
import {
  useArticleModalActions,
  useArticleModalSupportActions
} from './articleModalActionHooks';
import {
  useArticleModalArticleUpdates,
  useArticleModalFullscreen,
  useArticleModalSwipeNavigation,
  useArticleModalTags,
  useArticleModalUiState,
  useOpenKpiaModalEvent
} from './articleModalHooks';
import useArticleModalReadingTools from './useArticleModalReadingTools';

// Hooks
import {
  useMarkAsRead,
  useLazyLoadContent
} from '../../../../hooks';
import { isQualitativeAnalysisAiApplied } from '../../../../utils/aiAnalysisStatus';

const ArticleModal = ({
  article,
  open,
  onClose,
  onArticleUpdated,
  onToggleFavorite,
  onToggleRead,
  onUpdateSentiment,
  leftArrow,
  rightArrow,
  onPrev,
  onNext,
  onAddTags,
  onRemoveTag,
  onEdit,
  onDelete,
  onSendNewsletter,
  availableTags = []
}) => {
  // Hooks precisam ser chamados no nível superior, antes de qualquer condição
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const contentContainerRef = useRef(null);
  const [kpiaInitialSection, setKpiaInitialSection] = useState('smartscore');

  const { isSentimentAIEnabled } = useMIA();

  const { fontSize } = useViewSettings();
  const { fetchTags: refreshGlobalTags } = useTagSettings();

  // Custom hooks - Atomic Design
  const { localArticle, setLocalArticle, loading: loadingFullContent } = useLazyLoadContent(article, open);
  const timerRef = useMarkAsRead(article, open, onToggleRead);
  const {
    closeAnalysisMenus,
    closeExposureMenu,
    closeKpiaModal,
    closeMoreMenu,
    closeSentimentMenu,
    exposureAnchor,
    highlightEnabled,
    kpiaModalOpen,
    moreMenuAnchor,
    openExposureMenu,
    openKpiaModal,
    openMoreMenu,
    openSentimentMenu,
    sentimentAnchor,
    setKpiaModalOpen,
    showBriefingHighlights,
    toggleHighlight
  } = useArticleModalUiState();
  const { applyArticleUpdate } = useArticleModalArticleUpdates({
    article,
    availableTags,
    localArticle,
    onArticleUpdated,
    setLocalArticle
  });
  const {
    closeFullscreen,
    fullscreenOpen,
        fullscreenPageIndex,
    fullscreenZoom,
    openFullscreen,
    resetZoom,
    zoomIn,
    zoomOut
  } = useArticleModalFullscreen();
  const { handleSwipeEnd, handleSwipeStart } = useArticleModalSwipeNavigation({ onNext, onPrev });
  const {
    closeSimilarDialog,
    closeTagDialog,
    handleAddTags,
    handleRemoveTag,
    lastAddedTag,
    openTagDialog,
    similarDialogOpen,
    tagDialogOpen
  } = useArticleModalTags({
    article,
    applyArticleUpdate,
    onAddTags,
    onRemoveTag,
    refreshGlobalTags,
    setLocalArticle
  });
  useOpenKpiaModalEvent(openKpiaModal);

  const handleClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    closeKpiaModal();
    onClose();
  };

  const {
    collectionDialogOpen,
    handleAddToCollection,
    handleCollectionSuccess,
    handleCreateNewsletterFromShare,
    handleOpenPublicLink,
    handleShareMenuClose,
    handleShareMenuOpen,
    setCollectionDialogOpen,
    setSnackbarOpen,
    shareMenuAnchor,
    showSnackbar,
    snackbarMessage,
    snackbarOpen
  } = useArticleModalSupportActions({
    closeArticle: handleClose,
    localArticle,
    navigate,
    onSendNewsletter
  });

  const {
    exposureAnalyzing,
    handleAnalyzeExposure,
    handleAnalyzeSentiment,
    handleRunQualitativeAnalysis,
    handleToggleFavorite,
    handleToggleRead,
    handleUpdateExposure,
    handleUpdateSentiment,
    qualitativeAnalysisRunning,
    sentimentAnalyzing
  } = useArticleModalActions({
    applyArticleUpdate,
    isSentimentAIEnabled,
    localArticle,
    onToggleFavorite,
    onToggleRead,
    onUpdateSentiment,
    setKpiaModalOpen,
    setLocalArticle,
    showSnackbar
  });

  const {
    addHighlight,
    briefingKeywords,
    closeSearch,
    currentMatch,
    customFontSize,
    goToNextMatch,
    goToPrevMatch,
    handleListen,
    handleToggleSpeech,
    highlights,
    isPaused,
    isSearchOpen,
    isSpeaking,
    isTTSSupported,
    onChangeRate,
    openSearch,
    progress,
    rate,
    removeHighlight,
    searchTerm,
    semanticHighlights,
    setSearchTerm,
    stopTTS,
    totalMatches,
    youtubeId
  } = useArticleModalReadingTools({
    articleId: article?._id,
    contentContainerRef,
    fontSize,
    localArticle,
    open
  });

  if (!localArticle) return null;
  const qualitativeAnalysisApplied = isQualitativeAnalysisAiApplied(localArticle);
  const handleQualitativeAnalysisUpdated = (updatedArticle) => {
    if (updatedArticle) applyArticleUpdate(updatedArticle);
  };
  const handleOpenSmartScore = () => {
    setKpiaInitialSection('smartscore');
    openKpiaModal();
  };
  const handleOpenPerformance = () => {
    setKpiaInitialSection('performance');
    openKpiaModal();
  };
  const handleRemoveCurrentTag = onRemoveTag
    ? (tag) => handleRemoveTag(localArticle._id, tag)
    : null;
  const handleAnalyzeExposureAndClose = () => {
    handleAnalyzeExposure();
    closeAnalysisMenus();
  };
  const handleAnalyzeSentimentAndClose = () => {
    handleAnalyzeSentiment();
    closeAnalysisMenus();
  };
  const handleSelectExposure = (value) => {
    handleUpdateExposure(value);
    closeAnalysisMenus();
  };
  const handleSelectSentiment = (value) => {
    handleUpdateSentiment(localArticle._id, value);
    closeAnalysisMenus();
  };

  return (
    <ArticleModalView
      article={localArticle}
      availableTags={availableTags}
      auxiliary={{
        collectionDialogOpen,
        exposureAnchor,
        exposureAnalyzing,
        fullscreenOpen,
        fullscreenPageIndex,
        fullscreenZoom,
        lastAddedTag,
        rootArticleId: article?._id,
        sentimentAnchor,
        sentimentAnalyzing,
        shareMenuAnchor,
        similarDialogOpen,
        snackbarMessage,
        snackbarOpen,
        tagDialogOpen,
        onAddTags: handleAddTags,
        onAnalyzeExposure: handleAnalyzeExposureAndClose,
        onAnalyzeSentiment: handleAnalyzeSentimentAndClose,
        onCloseCollectionDialog: () => setCollectionDialogOpen(false),
        onCloseExposure: closeExposureMenu,
        onCloseFullscreen: closeFullscreen,
        onCloseSentiment: closeSentimentMenu,
        onCloseShare: handleShareMenuClose,
        onCloseSimilarDialog: closeSimilarDialog,
        onCloseSnackbar: () => setSnackbarOpen(false),
        onCloseTagDialog: closeTagDialog,
        onCollectionSuccess: handleCollectionSuccess,
        onCreateNewsletter: handleCreateNewsletterFromShare,
        onNotify: showSnackbar,
        onSelectExposure: handleSelectExposure,
        onSelectSentiment: handleSelectSentiment,
        onSimilarSuccess: (count) => {
          console.log(`Tag propagada para ${count} artigos similares`);
        },
        onZoomIn: zoomIn,
        onZoomOut: zoomOut,
        onZoomReset: resetZoom
      }}
      content={{
        loadingFullContent,
        loadedContentProps: {
          actionAnchorEl: moreMenuAnchor,
          briefingKeywords,
          contentContainerRef,
          currentMatch,
          fontSize: customFontSize,
          highlightEnabled,
          highlights,
          isMobile,
          isOpen: open,
          isPaused,
          isSearchOpen,
          isSpeaking,
          isTTSSupported,
          progress,
          rate,
          searchTerm,
          semanticHighlights,
          showBriefingHighlights,
          totalMatches,
          videoRef,
          youtubeId,
          onAddHighlight: addHighlight,
          onAddTag: openTagDialog,
          onAddToCollection: () => handleAddToCollection(localArticle),
          onAnalyzeKpia: handleRunQualitativeAnalysis,
          onBack: handleClose,
          onChangeRate,
          onClose: handleClose,
          onCloseActionMenu: closeMoreMenu,
          onCloseSearch: closeSearch,
          onDelete,
          onEdit,
          onListen: handleListen,
          onNextMatch: goToNextMatch,
          onOpenExposureMenu: openExposureMenu,
          onOpenFullscreen: openFullscreen,
          onOpenKpia: handleOpenSmartScore,
          onOpenPerformance: handleOpenPerformance,
          onOpenPublicLink: handleOpenPublicLink,
          onOpenSearch: openSearch,
          onOpenSentimentMenu: openSentimentMenu,
          onOpenShare: handleShareMenuOpen,
          onOpenTagDialog: openTagDialog,
          onPrevMatch: goToPrevMatch,
          onRecalculateKpia: handleQualitativeAnalysisUpdated,
          onRemoveHighlight: removeHighlight,
          onRemoveTag: handleRemoveCurrentTag,
          onSearchChange: setSearchTerm,
          onStopSpeech: stopTTS,
          onToggleFavorite: () => handleToggleFavorite(),
          onToggleHighlight: toggleHighlight,
          onToggleRead: () => handleToggleRead(),
          onToggleSpeech: handleToggleSpeech
        }
      }}
      dialog={{
        onClose: handleClose,
        onTouchEnd: handleSwipeEnd,
        onTouchStart: handleSwipeStart,
        open
      }}
      footer={{
        highlightEnabled,
        isPaused,
        isSearchOpen,
        isSpeaking,
        isTTSSupported,
        onAddToCollection: () => handleAddToCollection(localArticle),
        onAnalyzeKpia: handleRunQualitativeAnalysis,
        onDelete,
        onEdit,
        onListen: handleListen,
        onOpenExposureMenu: openExposureMenu,
        onOpenMore: openMoreMenu,
        onOpenPublicLink: handleOpenPublicLink,
        onOpenSearch: openSearch,
        onOpenShare: handleShareMenuOpen,
        onOpenTagDialog: openTagDialog,
        onOpenSentimentMenu: openSentimentMenu,
        onRemoveTag: handleRemoveCurrentTag,
        onToggleFavorite: handleToggleFavorite,
        onToggleHighlight: toggleHighlight,
        onToggleRead: handleToggleRead
      }}
      kpia={{
        initialSection: kpiaInitialSection,
        kpiaAnalyzing: qualitativeAnalysisRunning,
        onClose: closeKpiaModal,
        onRecalculate: handleQualitativeAnalysisUpdated,
        open: kpiaModalOpen,
        qualitativeAnalysisApplied
      }}
      navigation={{
        leftArrow,
        onNext,
        onPrev,
        open: open && !fullscreenOpen,
        rightArrow
      }}
    />
  );
};

ArticleModal.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    source: PropTypes.string,
    author: PropTypes.string,
    publishedAt: PropTypes.string,
    url: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.string,
        color: PropTypes.string,
        textColor: PropTypes.string
      })
    ])),
    isFavorite: PropTypes.bool,
    isRead: PropTypes.bool
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onArticleUpdated: PropTypes.func,
  onToggleFavorite: PropTypes.func,
  onToggleRead: PropTypes.func,
  leftArrow: PropTypes.bool,
  rightArrow: PropTypes.bool,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onAddTags: PropTypes.func,
  onRemoveTag: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSendNewsletter: PropTypes.func,
  availableTags: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string
  }))
};

ArticleModal.defaultProps = {
  onArticleUpdated: null
};

export default ArticleModal;


